/**
 * portalPromptPlacement — pure function for computing viewport-clamped
 * placement of portal instructions (e.g. "[W / Up] Enter Lanternwood").
 *
 * Prevents prompt text from clipping at the screen edge when teleporters
 * are positioned near map/viewport boundaries (such as Lanternroot Hollow
 * tp_lanternwood at x = 120), while strictly preserving world association
 * and distance visibility (off-screen portals scroll off-screen naturally).
 *
 * W1a (2026-09-28) — optional HUD avoidance: callers may pass screen-space
 * `avoidRects` (e.g. the target panel's frame). After edge clamping, an
 * on-screen prompt that would render behind a rect is shifted to a clear side,
 * still margin-contained; when no clear side fits the clamped placement is
 * returned unchanged. Off-screen anchors are never pulled in.
 */

import {
  computeWorldTransform,
  CanvasSize,
  CameraState,
} from './computeWorldTransform';
import type { HudOcclusionRect } from './hudOcclusionZones';

/** Shared screen-space rectangle shape (viewport logical pixels). */
export interface PortalPromptScreenBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface PortalPromptPlacementInput {
  /** The anchor position of the entity/prompt in world coordinates. */
  worldPosition: { x: number; y: number };
  /** Measured text dimensions in world units (from Pixi Text.width, Text.height). */
  textDimensions: { width: number; height: number };
  /** Text anchor (default { x: 0.5, y: 1 }). */
  anchor?: { x: number; y: number };
  /** Current canvas size (logical pixels). */
  canvasSize: CanvasSize;
  /** Current camera state. */
  cameraState: CameraState;
  /** Minimum margin from visible viewport edges in logical screen pixels. Default 16. */
  margin?: number;
  /** If true, only clamp horizontally. Default true. */
  horizontalOnly?: boolean;
  /** Optional uniform scale factor applied to the text (e.g. readable scale on mobile). */
  textScale?: number;
  /**
   * Optional screen-space rects (viewport logical px, top-left origin — the
   * same space this function's screen coordinates use) the prompt should not
   * render behind. Applied after clamping, on-screen anchors only.
   */
  avoidRects?: ReadonlyArray<HudOcclusionRect>;
}

export interface PortalPromptPlacementResult {
  /** The placed world position (clamped or original). */
  worldX: number;
  worldY: number;
  /** The screen position where the anchor point will be rendered. */
  screenX: number;
  /** The screen position where the anchor point will be rendered. */
  screenY: number;
  /** The screen bounds of the text rectangle. */
  screenBounds: PortalPromptScreenBounds;
  /** Whether horizontal clamping was applied. */
  isClampedX: boolean;
  /** Whether vertical clamping was applied. */
  isClampedY: boolean;
  /** Whether the entity anchor is currently inside the visible viewport. */
  isOnScreen: boolean;
  /** Whether the placement was shifted to clear an avoid rect (W1a). */
  isAvoided: boolean;
}

const DEFAULT_MARGIN = 16;
const DEFAULT_ANCHOR = { x: 0.5, y: 1 };
const EPSILON = 0.001;

function isUsableAvoidRect(rect: HudOcclusionRect): boolean {
  return (
    Number.isFinite(rect.left) &&
    Number.isFinite(rect.top) &&
    Number.isFinite(rect.right) &&
    Number.isFinite(rect.bottom) &&
    rect.right > rect.left &&
    rect.bottom > rect.top
  );
}

/** Strict overlap — touching edges do not count as an intersection. */
function boundsOverlapRect(bounds: PortalPromptScreenBounds, rect: HudOcclusionRect): boolean {
  return (
    bounds.left < rect.right &&
    bounds.right > rect.left &&
    bounds.top < rect.bottom &&
    bounds.bottom > rect.top
  );
}

/** True when `bounds` keeps at least `gap` px of separation from `rect`. */
function boundsClearOfRect(
  bounds: PortalPromptScreenBounds,
  rect: HudOcclusionRect,
  gap: number,
): boolean {
  return (
    bounds.right + gap <= rect.left ||
    bounds.left >= rect.right + gap ||
    bounds.bottom + gap <= rect.top ||
    bounds.top >= rect.bottom + gap
  );
}

export function computePortalPromptPlacement(
  input: PortalPromptPlacementInput,
): PortalPromptPlacementResult {
  const {
    worldPosition,
    textDimensions,
    canvasSize,
    cameraState,
    margin = DEFAULT_MARGIN,
    horizontalOnly = true,
  } = input;

  const anchor = input.anchor ?? DEFAULT_ANCHOR;
  const xform = computeWorldTransform(canvasSize, cameraState);

  // Fallback for uninitialized / degenerate render state
  if (xform.scale <= 0 || canvasSize.width <= 0 || canvasSize.height <= 0) {
    return {
      worldX: worldPosition.x,
      worldY: worldPosition.y,
      screenX: worldPosition.x,
      screenY: worldPosition.y,
      screenBounds: {
        left: worldPosition.x - anchor.x * textDimensions.width,
        right: worldPosition.x + (1 - anchor.x) * textDimensions.width,
        top: worldPosition.y - anchor.y * textDimensions.height,
        bottom: worldPosition.y + (1 - anchor.y) * textDimensions.height,
      },
      isClampedX: false,
      isClampedY: false,
      isOnScreen: true,
      isAvoided: false,
    };
  }

  // Intended frame bounds inside the canvas (letterbox / pillarbox / zoom aware)
  const renderedW = cameraState.width * xform.scale;
  const renderedH = cameraState.height * xform.scale;
  const viewportLeft = Math.max(0, xform.offsetX);
  const viewportRight = Math.min(canvasSize.width, xform.offsetX + renderedW);
  const viewportTop = Math.max(0, xform.offsetY);
  const viewportBottom = Math.min(canvasSize.height, xform.offsetY + renderedH);

  // Unconstrained screen position of the anchor point
  const unconstrainedScreenX = xform.worldX + worldPosition.x * xform.scale;
  const unconstrainedScreenY = xform.worldY + worldPosition.y * xform.scale;

  const isOnScreen =
    unconstrainedScreenX >= viewportLeft &&
    unconstrainedScreenX <= viewportRight &&
    unconstrainedScreenY >= viewportTop &&
    unconstrainedScreenY <= viewportBottom;

  // Consistent measurement contract:
  // textDimensions provides dimensions in world units (e.g. from Pixi Text.width / Text.height,
  // which already incorporate any explicit local label scale).
  // xform.scale converts world units to screen pixels.
  // textScale is an optional additional factor (default 1) if unscaled dimensions were passed.
  const textScale = input.textScale ?? 1;
  const screenTextW = textDimensions.width * xform.scale * textScale;
  const screenTextH = textDimensions.height * xform.scale * textScale;

  const unconstrainedLeft = unconstrainedScreenX - anchor.x * screenTextW;
  const unconstrainedRight = unconstrainedScreenX + (1 - anchor.x) * screenTextW;
  const unconstrainedTop = unconstrainedScreenY - anchor.y * screenTextH;
  const unconstrainedBottom = unconstrainedScreenY + (1 - anchor.y) * screenTextH;

  const minScreenX = viewportLeft + margin;
  const maxScreenX = viewportRight - margin;
  const minScreenY = viewportTop + margin;
  const maxScreenY = viewportBottom - margin;
  const availableWidth = Math.max(0, maxScreenX - minScreenX);

  // --- Horizontal Placement ---
  let clampedScreenX = unconstrainedScreenX;
  let isClampedX = false;

  // Clamping applies strictly to visible on-screen portals near viewport edges.
  // Distant or off-screen portals scroll naturally with the world and must never
  // be pulled into the viewport.
  if (unconstrainedScreenX >= viewportLeft && unconstrainedScreenX <= viewportRight) {
    if (availableWidth > 0 && screenTextW > availableWidth) {
      // Long label exceeding available viewport width: align start (left edge)
      // to minScreenX so the interaction prompt ([E / ↑] Enter...) is readable.
      const targetLeft = minScreenX;
      clampedScreenX = targetLeft + anchor.x * screenTextW;
      isClampedX = Math.abs(clampedScreenX - unconstrainedScreenX) > 0.001;
    } else if (unconstrainedLeft < minScreenX) {
      const shift = minScreenX - unconstrainedLeft;
      if (shift > 0.001) {
        clampedScreenX = unconstrainedScreenX + shift;
        isClampedX = true;
      }
    } else if (unconstrainedRight > maxScreenX) {
      const shift = unconstrainedRight - maxScreenX;
      if (shift > 0.001) {
        clampedScreenX = unconstrainedScreenX - shift;
        isClampedX = true;
      }
    }
  }

  // --- Vertical Placement (Optional) ---
  let clampedScreenY = unconstrainedScreenY;
  let isClampedY = false;

  if (!horizontalOnly && unconstrainedScreenY >= viewportTop && unconstrainedScreenY <= viewportBottom) {
    const availableHeight = Math.max(0, maxScreenY - minScreenY);
    if (availableHeight > 0 && screenTextH > availableHeight) {
      clampedScreenY = minScreenY + anchor.y * screenTextH;
      isClampedY = Math.abs(clampedScreenY - unconstrainedScreenY) > 0.001;
    } else if (unconstrainedTop < minScreenY) {
      const shift = minScreenY - unconstrainedTop;
      if (shift > 0.001) {
        clampedScreenY = unconstrainedScreenY + shift;
        isClampedY = true;
      }
    } else if (unconstrainedBottom > maxScreenY) {
      const shift = unconstrainedBottom - maxScreenY;
      if (shift > 0.001) {
        clampedScreenY = unconstrainedScreenY - shift;
        isClampedY = true;
      }
    }
  }

  // --- HUD Occlusion Avoidance (optional, W1a) ---
  // Runs AFTER edge clamping and only for anchors that are visible on screen.
  // Candidates are accepted only while the text stays inside the same
  // margin-respecting band the clamp enforces (horizontal candidates keep y;
  // vertical candidates are offered only when `horizontalOnly` is false, so
  // that contract is unchanged) and clear every avoid rect by >= margin. When
  // no candidate fits, the clamped placement is returned unchanged —
  // containment never regresses and off-screen anchors are never pulled in.
  let isAvoided = false;
  const rawAvoidRects = input.avoidRects;
  if (rawAvoidRects && rawAvoidRects.length > 0 && isOnScreen) {
    const boundsAt = (screenX: number, screenY: number): PortalPromptScreenBounds => ({
      left: screenX - anchor.x * screenTextW,
      right: screenX + (1 - anchor.x) * screenTextW,
      top: screenY - anchor.y * screenTextH,
      bottom: screenY + (1 - anchor.y) * screenTextH,
    });
    const currentBounds = boundsAt(clampedScreenX, clampedScreenY);
    // Validated inline (no per-call array allocation): this runs on the
    // per-tick render path.
    const intersectsAny = rawAvoidRects.some(
      rect => isUsableAvoidRect(rect) && boundsOverlapRect(currentBounds, rect),
    );
    if (intersectsAny) {
      const gap = Number.isFinite(margin) ? Math.max(0, margin) : DEFAULT_MARGIN;
      // Nearest-side order: right, left, then (when vertical placement is
      // enabled) below, above. Strictly-closer wins; ties keep the earlier side.
      const candidates: Array<{ x: number; y: number }> = [];
      for (const rect of rawAvoidRects) {
        if (!isUsableAvoidRect(rect) || !boundsOverlapRect(currentBounds, rect)) continue;
        candidates.push(
          { x: rect.right + gap + anchor.x * screenTextW, y: clampedScreenY },
          { x: rect.left - gap - (1 - anchor.x) * screenTextW, y: clampedScreenY },
        );
        if (!horizontalOnly) {
          candidates.push(
            { x: clampedScreenX, y: rect.bottom + gap + anchor.y * screenTextH },
            { x: clampedScreenX, y: rect.top - gap - (1 - anchor.y) * screenTextH },
          );
        }
      }

      let bestX = clampedScreenX;
      let bestY = clampedScreenY;
      let bestDistanceSq = Infinity;
      for (const candidate of candidates) {
        const bounds = boundsAt(candidate.x, candidate.y);
        const horizontallyContained =
          bounds.left >= minScreenX - EPSILON && bounds.right <= maxScreenX + EPSILON;
        if (!horizontallyContained) continue;
        if (Math.abs(candidate.y - clampedScreenY) > EPSILON) {
          const verticallyContained =
            bounds.top >= minScreenY - EPSILON && bounds.bottom <= maxScreenY + EPSILON;
          if (!verticallyContained) continue;
        }
        if (!rawAvoidRects.every(
          rect => !isUsableAvoidRect(rect) || boundsClearOfRect(bounds, rect, gap),
        )) continue;
        const dx = candidate.x - clampedScreenX;
        const dy = candidate.y - clampedScreenY;
        const distanceSq = dx * dx + dy * dy;
        if (distanceSq < bestDistanceSq) {
          bestDistanceSq = distanceSq;
          bestX = candidate.x;
          bestY = candidate.y;
        }
      }

      if (bestDistanceSq < Infinity) {
        isAvoided = true;
        clampedScreenX = bestX;
        clampedScreenY = bestY;
        isClampedX = isClampedX || Math.abs(bestX - unconstrainedScreenX) > EPSILON;
        isClampedY = isClampedY || Math.abs(bestY - unconstrainedScreenY) > EPSILON;
      }
    }
  }

  // Map clamped screen position back to world coordinates
  const worldX = (clampedScreenX - xform.worldX) / xform.scale;
  const worldY = (clampedScreenY - xform.worldY) / xform.scale;

  const finalLeft = clampedScreenX - anchor.x * screenTextW;
  const finalRight = clampedScreenX + (1 - anchor.x) * screenTextW;
  const finalTop = clampedScreenY - anchor.y * screenTextH;
  const finalBottom = clampedScreenY + (1 - anchor.y) * screenTextH;

  return {
    worldX,
    worldY,
    screenX: clampedScreenX,
    screenY: clampedScreenY,
    screenBounds: {
      left: finalLeft,
      right: finalRight,
      top: finalTop,
      bottom: finalBottom,
    },
    isClampedX,
    isClampedY,
    isOnScreen,
    isAvoided,
  };
}
